import device from "current-device";
import React, { createRef } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Waypoint } from "react-waypoint";
import { bindActionCreators, Dispatch } from "redux";
import { SecondaryButton } from "Shared/Buttons";
import { CalculatedPaymentProps } from "Shared/CalculatedPayment";
import { analyticsFEA } from "src/AnalyticsFEA";
import { ConditionalRebatesPanel } from "src/components/ConditionalRebates";
import CustomerRequestedTaxes from "src/components/CustomizePayment/CustomerRequestedTaxes/index";
import LockedPricingSnackBar from "src/components/CustomizePayment/LockedPricingSnackBar";
import CreditRatingRow from "src/components/CustomizePayment/PaymentFields/CreditRatingRow";
import LeaseFields from "src/components/CustomizePayment/PaymentFields/LeaseFields";
import LoanFields from "src/components/CustomizePayment/PaymentFields/LoanFields";
import { ApplicationStore } from "src/store";
import { setGarageCreated } from "src/store/actions/onlineShopper";
import { patchPaymentsRequest } from "src/store/actions/payments";
import { isLockedPricingSetup } from "src/store/selectors/account";
import { isConditionalRebatesEnabled } from "src/store/selectors/conditionalRebates";
import { isCalculateTaxesCustomerRequest } from "src/store/selectors/financeSettings";
import {
    getPaymentTypeMatrix,
    getSelectedPaymentType,
    getVehicle,
    getVehiclesArrayForFEA,
} from "src/store/selectors/vehicles";
import { breakpoints } from "src/styles/createBreakpoints";
import { getAccountDisclaimer } from "src/utils/account";
import { getViewportWidth } from "src/utils/browser";
import { scrollWindowForIOsBy, scrollWindowForIOsToTop } from "src/utils/scroll";
import { PaymentTypeOptions } from "Types/state";
import CalculatedPayment from "../../containers/CalculatedPayment";
import TradePendingContainer from "../TradePending";
import "./CustomizePayment.scss";
import CustomizePaymentHeader from "./CustomizePaymentHeader";
import "./PaymentFields/PaymentFields.scss";
import PaymentType from "./PaymentType";
import ZipCodeRow from "./ZipCode/ZipCodeRow";
import withFrame, { WithFrameProps } from "src/providers/frame/withFrame";
import { setLocalDealState } from "src/utils/localStorage";

const mobileBreakpoint = breakpoints.sm;
const desktopBreakpoint = breakpoints.md;
const smallHeaderHeight = 110;
const largeHeaderHeight = 140;

interface CustomizePaymentState {
    customizePaymentHeaderHeight: number;
    paymentInHeader: boolean;
    viewportWidth: number;
    fixedHeader: boolean;
    paymentBuilderLoadFired: boolean;
}

type CustomizePaymentProps = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> &
    WithFrameProps;

class CustomizePayment extends React.Component<CustomizePaymentProps, CustomizePaymentState> {
    private customizePaymentHeader = createRef<typeof CustomizePaymentHeader>();
    private contentContainerRef = createRef<HTMLDivElement>();
    private marginTop: number;

    constructor(props: CustomizePaymentProps) {
        super(props);

        this.state = {
            customizePaymentHeaderHeight: largeHeaderHeight,
            paymentInHeader: false,
            viewportWidth: getViewportWidth(),
            fixedHeader: false,
            paymentBuilderLoadFired: false,
        };
    }

    public componentDidMount() {
        // @ts-ignore
        this.props.iframe.window.addEventListener("resize", this.updateViewportWidth);
    }

    public componentDidUpdate(
        prevProps: Readonly<CustomizePaymentProps>,
        prevState: Readonly<CustomizePaymentState>,
        snapshot?: any
    ) {
        if (this.props.deal.uuid !== undefined && !this.state.paymentBuilderLoadFired) {
            analyticsFEA.engine.paymentBuilderLoad(this.props.deal.uuid, this.props.vehiclesForFEA);
            this.setState({ paymentBuilderLoadFired: true });
        }
    }

    public componentWillUnmount() {
        // @ts-ignore
        this.props.iframe.window.removeEventListener("resize", this.updateViewportWidth);
    }

    public render() {
        const { paymentInHeader, viewportWidth, fixedHeader } = this.state;

        if (viewportWidth < desktopBreakpoint) {
            if (viewportWidth < mobileBreakpoint) {
                this.marginTop = smallHeaderHeight;
            } else {
                this.marginTop = largeHeaderHeight;
            }
        } else {
            this.marginTop = fixedHeader ? largeHeaderHeight : 0;
        }

        return (
            <div className="CustomizePayment">
                <Waypoint onEnter={this.handleHeaderWaypoint(false)} onLeave={this.handleHeaderWaypoint(true)} />

                <CustomizePaymentHeader
                    vin={this.props.vin}
                    account={this.props.account}
                    accountDisplayed={desktopBreakpoint < viewportWidth}
                    calculatedPaymentRenderer={paymentInHeader ? this.renderCalculatedPayment : undefined}
                    // @ts-ignore
                    ref={this.customizePaymentHeader}
                    fixed={fixedHeader}
                    style={{
                        minHeight: `${
                            desktopBreakpoint > viewportWidth
                                ? paymentInHeader
                                    ? largeHeaderHeight
                                    : smallHeaderHeight
                                : largeHeaderHeight
                        }px`,
                    }}
                />

                <div
                    style={{
                        marginTop: `${this.marginTop}px`,
                    }}
                    ref={this.contentContainerRef}
                    data-e2e="customizePaymentBody"
                >
                    {this.renderContent()}
                </div>
            </div>
        );
    }

    public renderCalculatedPayment = (extraProps?: CalculatedPaymentProps) => {
        const { viewportWidth } = this.state;
        const props = {
            selectedPaymentIndex: this.props.paymentType,
            showAPR: true,
            displayPaymentInfo: desktopBreakpoint <= viewportWidth,
            ...extraProps,
        };
        return <CalculatedPayment {...props} />;
    };

    public renderContent = () => {
        const { account, paymentType, paymentTypeDisplay, customerRequestedTaxes } = this.props;
        return (
            <div className="CustomizePayment__content">
                <Waypoint
                    topOffset={`${this.marginTop + this.marginTop / 2}px`}
                    onEnter={this.handlePaymentWaypoint(false)}
                    onLeave={this.handlePaymentWaypoint(true)}
                >
                    <CalculatedPayment selectedPaymentIndex={paymentType} showAPR={true} />
                </Waypoint>

                <LockedPricingSnackBar />

                <PaymentType
                    onChange={this.updatePaymentType}
                    enabledPayments={paymentTypeDisplay}
                    selectedPayment={paymentType}
                />

                {!customerRequestedTaxes && <ZipCodeRow />}

                {paymentType !== "cash" && (
                    <div className="PaymentFields">
                        {paymentType === "lease" && <LeaseFields vin={this.props.vin} />}
                        {paymentType === "loan" && <LoanFields vin={this.props.vin} />}
                        <CreditRatingRow />
                        <ConditionalRebatesPanel />
                    </div>
                )}

                {customerRequestedTaxes && <CustomerRequestedTaxes />}

                <TradePendingContainer onFocus={this.handleTradePendingOnFocus} />

                <div className="CustomizePayment__button--container">
                    <Link to={this.props.isLockedPricing ? "my-garage" : "create"}>
                        <SecondaryButton
                            className="CustomizePayment__button--save"
                            data-e2e="save-and-compare"
                            onClick={this.handleSaveAndCompareClick}
                        >
                            {getAccountDisclaimer(account)("payment_calculator_cta")(
                                "CustomizePayment.savePaymentButtonLabel"
                            )}
                        </SecondaryButton>
                    </Link>
                </div>
            </div>
        );
    };

    public updateViewportWidth = (e: any) => {
        this.setState({
            viewportWidth: getViewportWidth(),
        });
    };

    public handleHeaderWaypoint = (value: boolean) => () => {
        this.setState({
            fixedHeader: value,
        });
    };

    public handlePaymentWaypoint = (value: boolean) => () => {
        this.setState({
            paymentInHeader: value,
        });
    };

    protected updatePaymentType = (paymentType: PaymentTypeOptions) => {
        this.props.patchPaymentsRequest(
            this.props.deal.uuid as string,
            this.props.vin,
            {
                selected: paymentType,
            },
            "set_payment_type"
        );

        analyticsFEA.engine.calculationStart(this.props.deal.uuid as string, [this.props.vehicle]);
    };

    protected handleSaveAndCompareClick = () => {
        analyticsFEA.engine.savesPayment();
        analyticsFEA.engine.calculationFinish(this.props.deal.uuid as string, [this.props.vehicle]);

        if (this.props.isLockedPricing) {
            // Update local storage to remember that the user has created a garage
            setLocalDealState({
                slug: this.props.account.slug,
                env: this.props.env,
                uuid: this.props.deal.uuid!,
                garageCreated: true,
                isAuthed: true,
            });
            this.props.setGarageCreated();
        }
    };

    protected handleTradePendingOnFocus = (e: React.FocusEvent<HTMLElement>, linkHash: string) => {
        const anchor = this.props.iframe.document.getElementById(linkHash) as HTMLDivElement;

        if (anchor) {
            const container = this.contentContainerRef.current;

            if (container) {
                setTimeout(async () => {
                    if (device.ios()) {
                        await this.handleFocusForIOs(container, anchor);
                    } else {
                        const point = anchor.offsetTop - this.state.customizePaymentHeaderHeight - 10;
                        container.scrollTo && container.scrollTo(0, point);
                    }
                }, 200);
            }
        }
    };

    protected async handleFocusForIOs(container: HTMLDivElement, anchor: HTMLElement) {
        await scrollWindowForIOsToTop();

        const TIMEOUT = 80;
        const OFFSET = 10;

        setTimeout(() => {
            container.scrollTo && container.scrollTo(0, container.scrollHeight);

            setTimeout(() => {
                const headerHeight = this.state.customizePaymentHeaderHeight;

                const { top: top1 } = anchor.getBoundingClientRect();

                container.scrollBy(0, top1 - headerHeight - OFFSET);

                setTimeout(() => {
                    const { top: top2 } = anchor.getBoundingClientRect();

                    if (top2 < headerHeight) {
                        container.scrollBy(0, -top2);
                    } else if (top2 - headerHeight < OFFSET) {
                        scrollWindowForIOsBy(headerHeight);
                    } else {
                        scrollWindowForIOsBy(top2 - OFFSET);
                    }
                }, TIMEOUT);
            }, TIMEOUT);
        }, TIMEOUT);
    }
}

function mapStateToProps(state: ApplicationStore) {
    const { account, deal, onlineShopper } = state;
    const vin = onlineShopper.toggledVin!;
    return {
        vin,
        env: state.onlineShopper.env,
        vehicle: getVehicle(state, vin),
        vehiclesForFEA: getVehiclesArrayForFEA(state),
        account,
        deal,
        hasTradeIn: onlineShopper.hasTradeIn,
        paymentType: getSelectedPaymentType(state, vin),
        paymentTypeDisplay: getPaymentTypeMatrix(state, vin),
        isConditionalRebatesEnabled: isConditionalRebatesEnabled(state),
        isLockedPricing: isLockedPricingSetup(state),
        customerRequestedTaxes: isCalculateTaxesCustomerRequest(state),
    };
}

function mapDispatchToProps(dispatch: Dispatch) {
    return bindActionCreators({ patchPaymentsRequest, setGarageCreated }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(withFrame(CustomizePayment));
